import { Badge, Box, Text } from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import { FiEdit } from 'react-icons/fi'
import { DataTable } from '@/components/common/DataTable'
import type {
  ActionButton,
  ColumnConfig,
  FilterConfig,
} from '@/components/common/DataTable/types'
import type { SupportReceive } from '@/types'
import { formatDateTime } from '@/utils/formatUtils'
import {
  formatPayloadPreview,
  parseJson,
  SUPPORT_SOURCES,
  SUPPORT_STATUSES,
} from './supportPageHelpers'

interface SupportReceivesTabProps {
  t: TFunction
  receives: SupportReceive[]
  isReceivesLoading: boolean
  receivesError: Error | null
  filters: Record<string, string>
  onFiltersChange: (next: Record<string, string>) => void
  pagination?: {
    page: number
    limit: number
    total: number
  }
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  onEditReceive: (row: SupportReceive) => void
  onRefresh: () => void
  isRefreshing: boolean
}

export function SupportReceivesTab({
  t,
  receives,
  isReceivesLoading,
  receivesError,
  filters,
  onFiltersChange,
  pagination,
  onPageChange,
  onPageSizeChange,
  onEditReceive,
  onRefresh,
  isRefreshing,
}: SupportReceivesTabProps) {
  const columns: ColumnConfig<SupportReceive>[] = [
    {
      key: 'ticketId',
      title: t('support.receives.table.ticketId'),
      width: '18%',
      render: (value) => (
        <Text fontSize="sm" fontFamily="mono">
          {value || '—'}
        </Text>
      ),
    },
    {
      key: 'eventType',
      title: t('support.receives.table.eventType'),
      width: '14%',
      render: (value) => (
        <Text fontSize="sm" fontFamily="mono">
          {value || '—'}
        </Text>
      ),
    },
    {
      key: 'ticketStatus',
      title: t('support.receives.table.status'),
      width: '10%',
      render: (value) => (
        <Badge colorScheme="blue" textTransform="capitalize">
          {value || 'received'}
        </Badge>
      ),
    },
    {
      key: 'payloadJson',
      title: t('support.receives.table.summary'),
      width: '28%',
      render: (_, row) => {
        const parsed = parseJson(row.payloadJson ?? null)
        const preview = formatPayloadPreview(parsed)
        return (
          <Box>
            <Text fontSize="sm" fontWeight="semibold">
              {preview.title}
            </Text>
            <Text fontSize="xs" color="gray.500" noOfLines={2}>
              {preview.subtitle || '—'}
            </Text>
          </Box>
        )
      },
    },
    {
      key: 'receivedAt',
      title: t('support.receives.table.receivedAt'),
      width: '16%',
      render: (value) => <Text fontSize="sm">{formatDateTime(value as string)}</Text>,
    },
    {
      key: 'supportNotes',
      title: t('support.receives.table.notes'),
      width: '14%',
      render: (value) => (
        <Text fontSize="sm" noOfLines={2}>
          {value || '—'}
        </Text>
      ),
    },
  ]

  const filtersConfig: FilterConfig[] = [
    {
      key: 'status',
      type: 'select',
      label: t('support.receives.filters.status'),
      options: SUPPORT_STATUSES.map((status) => ({
        value: status,
        label: status.replace(/_/g, ' '),
      })),
    },
    {
      key: 'source',
      type: 'select',
      label: t('support.receives.filters.source'),
      options: [
        { value: '', label: t('common.all') },
        ...SUPPORT_SOURCES.map((source) => ({
          value: source,
          label: source,
        })),
      ],
    },
    {
      key: 'eventType',
      type: 'text',
      label: t('support.receives.filters.eventType'),
      placeholder: t('support.receives.filters.eventTypePlaceholder'),
    },
    {
      key: 'ticketId',
      type: 'text',
      label: t('support.receives.filters.ticketId'),
      placeholder: t('support.receives.filters.ticketIdPlaceholder'),
    },
    {
      key: 'sourceId',
      type: 'text',
      label: t('support.receives.filters.sourceId'),
      placeholder: t('support.receives.filters.sourceIdPlaceholder'),
    },
  ]

  const actions = (row: SupportReceive): ActionButton[] => [
    {
      label: t('common.edit'),
      onClick: () => onEditReceive(row),
      icon: <FiEdit />,
      size: 'xs',
      variant: 'outline',
      colorScheme: 'blue',
    },
  ]

  return (
    <DataTable
      data={receives}
      loading={isReceivesLoading}
      error={receivesError}
      columns={columns}
      filters={filtersConfig}
      initialFilters={filters}
      onFiltersChange={(next) => onFiltersChange(next as Record<string, string>)}
      pagination={
        pagination
          ? {
              page: pagination.page,
              pageSize: pagination.limit,
              total: pagination.total,
              pageSizeOptions: [10, 20, 50, 100],
            }
          : undefined
      }
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      actions={actions}
      showRefresh
      onRefresh={onRefresh}
      refreshing={isRefreshing}
      emptyMessage={t('support.receives.empty')}
    />
  )
}
